import { useState, useRef, useEffect, useCallback } from 'react';
import { headerIds } from 'constants/test-constants';

const SCROLL_DEBOUNCE_MS = 16;
const SCROLL_THRESHOLD = 50;

interface UseScrollHideHeaderOptions {
  enabled: boolean;
  scrollSource?: 'window' | 'element';
}

interface UseScrollHideHeaderResult {
  isFilterBarVisible: boolean;
  isHeaderVisible: boolean;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
}

/**
 * Hides the site header and filter bar when user scrolls down
 * and shows them again on scroll up. 
 */
export function useScrollHideHeader({
  enabled,
  scrollSource = 'window',
}: UseScrollHideHeaderOptions): UseScrollHideHeaderResult {
  const [isFilterBarVisible, setIsFilterBarVisible] = useState(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollTop = useRef(0);
  const scrollDebounceTimeout = useRef<NodeJS.Timeout>();

  const processScroll = useCallback((currentScrollTop: number) => {
    if (scrollDebounceTimeout.current) {
      clearTimeout(scrollDebounceTimeout.current);
    }

    scrollDebounceTimeout.current = setTimeout(() => {
      const isScrollingDown = currentScrollTop > lastScrollTop.current && currentScrollTop > SCROLL_THRESHOLD;
      const isScrollingUp = currentScrollTop < lastScrollTop.current;

      if (isScrollingDown) {
        setIsFilterBarVisible(prev => prev ? false : prev);
        setIsHeaderVisible(prev => prev ? false : prev);
      } else if (isScrollingUp) {
        setIsFilterBarVisible(prev => prev ? prev : true);
        setIsHeaderVisible(prev => prev ? prev : true);
      }

      lastScrollTop.current = currentScrollTop;
    }, SCROLL_DEBOUNCE_MS);
  }, []);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (!enabled) return;
    processScroll(event.currentTarget.scrollTop);
  }, [enabled, processScroll]);

  // Window scroll listener
  useEffect(() => {
    if (!enabled || scrollSource !== 'window') {
      if (!enabled) {
        setIsFilterBarVisible(true);
        setIsHeaderVisible(true);
      }
      return;
    }

    const onWindowScroll = () => processScroll(window.scrollY);

    window.addEventListener('scroll', onWindowScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onWindowScroll);
      if (scrollDebounceTimeout.current) {
        clearTimeout(scrollDebounceTimeout.current);
      }
    };
  }, [enabled, scrollSource, processScroll]);

  useEffect(() => {
    const headerElement = document.querySelector(`[data-testid="${headerIds.header}"]`) as HTMLElement;
    if (!headerElement) return;

    if (enabled) {
      headerElement.style.transition = 'transform .3s ease-in-out';
      headerElement.style.transform = isHeaderVisible ? 'translateY(0)' : 'translateY(-100%)';
    } else {
      headerElement.style.transform = 'translateY(0)';
    }
  }, [isHeaderVisible, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollDebounceTimeout.current) {
        clearTimeout(scrollDebounceTimeout.current);
      }
      const headerElement = document.querySelector(`[data-testid="${headerIds.header}"]`) as HTMLElement;
      if (headerElement) {
        headerElement.style.transform = 'translateY(0)';
      }
    };
  }, []);

  return { isFilterBarVisible, isHeaderVisible, handleScroll };
}
